import React from 'react';
import {
    Box,
    FormControl,
    FormHelperText,
    Grid,
    TextField,
    Link,
    Typography,
    useTheme,
    LinearProgress,
    Button
} from '@mui/material';
import * as settingService from 'services/settings-service';
import * as tenantService from 'services/tenant-service';
import { useTranslation } from 'react-i18next';
import * as Styles from 'pages/development/settings/Settings.styles';
import { IconContext } from 'react-icons';
import {AiFillMail } from 'react-icons/ai';
import { IPropsSettings } from '../Settings';
import { useAuthContext } from 'contexts/authContext';
import { tenantEmailDto } from 'services/tenant-service';


const EmailTab = (props: IPropsSettings) => {
    const { tenantId } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const { checkAuth } = useAuthContext();
    const [emails, setEmails] = React.useState<{ id: string, name: string }[]>([]);
    const [emailAddressFrom, setEmailAddressFrom] = React.useState<string>('');
    const [emailAddressInfoEmployee, setEmailAddressInfoEmployee] = React.useState<string>('');
    const [loading, setLoading] = React.useState<boolean>(false);

    const [newEmails, setNewEmails] = React.useState<tenantEmailDto>({ emailFrom: '', emailInfoEmployee: '' });

    React.useEffect(() => {
        setLoading(true);
        settingService.getEmailNames(tenantId).then((names) => {
            const files: typeof emails = [];
            Object.keys(names).forEach((key) => {
                const value = names[key];
                files.push({ id: key, name: value })
            });
            setEmails(files);
        });

        tenantService.getEmails(tenantId).then((zoneInfo) => {
            setEmailAddressFrom(zoneInfo.emailFrom);
            setEmailAddressInfoEmployee(zoneInfo.emailInfoEmployee);
            setNewEmails({ emailFrom: zoneInfo.emailFrom, emailInfoEmployee: zoneInfo.emailInfoEmployee });
            setLoading(false);
        });
    }, [tenantId]);

    const handleFileDownload = (fileName: string) => {
        settingService.downloadEmail(fileName, tenantId).then((res: Blob) => {
            if (res !== undefined) {
                var fileUrl = URL.createObjectURL(res);
                const link = document.createElement("a");
                link.href = fileUrl;
                link.download = fileName;
                link.click();
            }
        });
    }

    const handleUpdate = () => {
        tenantService.updateEmails(tenantId, newEmails).then(() => {
            setEmailAddressFrom(newEmails?.emailFrom);
            setEmailAddressInfoEmployee(newEmails?.emailInfoEmployee);
        });
    }

    const handleCancel = () => {
        setNewEmails({ emailFrom: emailAddressFrom, emailInfoEmployee: emailAddressInfoEmployee });
    }

    return (
        <Box>
            <Typography variant='h4'>
                {t('settings.email.title')}
            </Typography>
            <Box>
                {t('settings.email.description')}
            </Box>
            {loading && (
                <Box sx={{ marginTop: '1rem' }}>
                    <LinearProgress/>
                </Box>
            )}
            {!loading && (
                <Box sx={{ marginTop: '1rem' }}>
                    <Grid container spacing={2} sx={ Styles.infoWrapper(theme) }>
                        <Grid item xs={12} md={3}>
                            <Box>
                                {t('settings.email.sender')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <TextField
                                disabled={!checkAuth(['TenantAdministrator'])}
                                value={newEmails.emailFrom}
                                size='small'
                                // helperText={t('settings.email.senderHelper')}
                                onChange={(e) => setNewEmails({ ...newEmails, emailFrom: e.target.value })}
                                sx={{ backgroundColor: !checkAuth(['TenantAdministrator']) ? 'unset' : 'white', width: '50%' }}
                            />
                            <FormControl>
                                <FormHelperText>
                                    {t('settings.email.senderHelper')}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box>
                                {t('settings.email.destination')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <TextField 
                                disabled={!checkAuth(['TenantAdministrator'])}
                                value={newEmails.emailInfoEmployee} 
                                size='small' 
                                // helperText={t('settings.email.destinationHelper')} 
                                onChange={(e) => setNewEmails({ ...newEmails, emailInfoEmployee: e.target.value })}
                                sx={{ backgroundColor: !checkAuth(['TenantAdministrator']) ? 'unset' : 'white', width: '50%' }}
                            />
                            <FormControl>
                                <FormHelperText>
                                    {t('settings.email.destinationHelper')}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={ Styles.infoWrapper(theme) }>
                        <Grid item xs={12} md={3}>
                            <Box>
                                {t('settings.email.templates')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            {emails.map((email, index) => (
                                <Link style={{width: 'fit-content', display: 'block'}} key={index} href={`#?download=${email.name}`} onClick={() => handleFileDownload(email.id)}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                        <IconContext.Provider
                                            value={{
                                                size: "17px",
                                                style: { marginRight: "5px", alignSelf: "center" },
                                                color: "grey",
                                            }}
                                        >
                                            <Box component={AiFillMail}/>
                                        </IconContext.Provider>
                                        {t('emails.' + email.name)}
                                    </Box>
                                </Link>
                            ))}
                            <FormControl>
                                <FormHelperText>
                                    {t('settings.email.templatesHelper')}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    </Grid>
                    {(
                        newEmails.emailFrom !== emailAddressFrom ||
                        newEmails.emailInfoEmployee !== emailAddressInfoEmployee
                    ) &&
                        <Grid item xs={12} md={12} sx={{
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <Button
                                variant='contained'
                                color='cancel'
                                size='large'
                                onClick={handleCancel}
                                sx={{
                                    marginRight: '1rem'
                                }}
                            >
                                {t('buttons.cancel')}
                            </Button>
                            <Button
                                variant='contained'
                                color='success'
                                size='large'
                                onClick={handleUpdate}
                                sx={{
                                    marginLeft: '1rem'
                                }}
                            >
                                {t('buttons.save')}
                            </Button>
                        </Grid>
                    }
                </Box>
            )}
        </Box>
    )
}

export default EmailTab;